import { API, API_BASE } from '../config/api';
import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';


const PlayerContext = createContext(null);

// Normalize audio URLs to current origin so audio loads same-origin on any domain (iomsl.com, preview, etc.)
const normalizeAudioUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${window.location.origin}${url}`;
  const filesMatch = url.match(/(\/api\/files\/.+)$/);
  if (filesMatch) return `${window.location.origin}${filesMatch[1]}`;
  return url;
};

export function PlayerProvider({ children }) {
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const currentTrackRef = useRef(null);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  // Viewer-curated queue. Items are pushed here via "Add to Queue" buttons
  // anywhere in the app (Tracks, Beats, Free Media). When the current track
  // ends, we play the next queue item before falling back to random shuffle.
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const audioRef = useRef(new Audio());
  // Attach audio element to the DOM (hidden) so headless tests / dev tools can
  // observe currentTime, pause events, and the 30s snippet cap. Side-effect:
  // also exposes the audio + the snippet limit on `window.__player` for tests.
  useEffect(() => {
    const audio = audioRef.current;
    audio.id = 'mp-global-audio';
    audio.setAttribute('data-testid', 'global-player-audio');
    audio.style.cssText = 'position:fixed;width:1px;height:1px;left:-9999px;top:0;';
    if (!audio.parentNode) document.body.appendChild(audio);
    if (typeof window !== 'undefined') {
      window.__player = { audio, snippetLimit: 30 };
    }
    return () => {
      if (audio.parentNode === document.body) document.body.removeChild(audio);
      if (typeof window !== 'undefined') delete window.__player;
    };
  }, []);
  const tracksRef = useRef([]);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);

  // Snippet/Unlock state
  const SNIPPET_LIMIT_SECONDS = 30;
  const [snippetUnlockTrack, setSnippetUnlockTrack] = useState(null); // track to upsell
  // Once the user dismisses the unlock modal for a track, don't re-open it on
  // subsequent timeupdate ticks while the audio sits paused at 30s. It only
  // reopens if the user starts a NEW play session (playTrack clears this).
  const dismissedForRef = useRef(new Set());
  const unlockedIdsRef = useRef(new Set(
    (() => {
      try { return JSON.parse(localStorage.getItem('mp_unlockedTrackIds') || '[]'); }
      catch { return []; }
    })()
  ));
  const markUnlocked = useCallback((id) => {
    if (!id) return;
    unlockedIdsRef.current.add(String(id));
    try {
      localStorage.setItem('mp_unlockedTrackIds', JSON.stringify(Array.from(unlockedIdsRef.current)));
    } catch (e) { /* ignore quota */ }
  }, []);
  const isUnlocked = useCallback((id) => unlockedIdsRef.current.has(String(id)), []);

  // Set up Web Audio API analyser ONLY on user interaction (mobile requires this)
  const ensureAudioContext = useCallback(() => {
    if (sourceNodeRef.current) {
      // Already connected — just resume if needed
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      return;
    }
    try {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      sourceNodeRef.current = source;
      analyserRef.current = analyser;
    } catch (e) {
      // createMediaElementSource can only be called once — if it fails,
      // audio still plays through default output, just no visualizer
      console.warn('Web Audio setup skipped:', e.message);
    }
  }, []);

  // Pick a random playable track (has actual audio file URL, not a web page)
  const pickRandomTrack = useCallback((exclude) => {
    const playable = tracksRef.current.filter(t =>
      t.preview_url &&
      t.id !== exclude?.id &&
      (t.preview_url.endsWith('.mp3') || t.preview_url.endsWith('.wav') ||
       t.preview_url.endsWith('.ogg') || t.preview_url.endsWith('.m4a') ||
       t.preview_url.includes('/api/files/'))
    );
    if (playable.length === 0) return null;
    return playable[Math.floor(Math.random() * playable.length)];
  }, []);

  // Play next track: prefer the user queue, then fall back to random shuffle.
  const playNextRandom = useCallback(async () => {
    let next = null;
    if (queueRef.current.length > 0) {
      next = queueRef.current[0];
      const remaining = queueRef.current.slice(1);
      queueRef.current = remaining;
      setQueue(remaining);
    } else {
      next = pickRandomTrack(currentTrack);
    }
    if (next) {
      setCurrentTrack(next);
      audioRef.current.src = normalizeAudioUrl(next.preview_url);
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        if (next.id) axios.post(`${API}/tracks/${next.id}/play`).catch(() => {});
      } catch {
        setIsPlaying(false);
      }
    } else {
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [currentTrack, pickRandomTrack]);

  // ─── Queue helpers (exposed via context) ───
  const addToQueue = useCallback((track) => {
    if (!track || !track.preview_url) return false;
    // Skip duplicates already queued.
    if (queueRef.current.some(t => t.id && t.id === track.id)) return false;
    const item = { ...track, preview_url: normalizeAudioUrl(track.preview_url), _queueId: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` };
    queueRef.current = [...queueRef.current, item];
    setQueue(queueRef.current);
    return true;
  }, []);

  const removeFromQueue = useCallback((queueId) => {
    queueRef.current = queueRef.current.filter(t => t._queueId !== queueId);
    setQueue(queueRef.current);
  }, []);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    setQueue([]);
  }, []);

  const moveInQueue = useCallback((queueId, dir) => {
    const idx = queueRef.current.findIndex(t => t._queueId === queueId);
    if (idx < 0) return;
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= queueRef.current.length) return;
    const next = [...queueRef.current];
    [next[idx], next[target]] = [next[target], next[idx]];
    queueRef.current = next;
    setQueue(next);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Snippet enforcement — if current track is in snippet mode and not unlocked, stop at 30s
      const ct = currentTrackRef.current;
      if (ct && ct.preview_mode === 'snippet' && !isUnlocked(ct.id) && audio.currentTime >= SNIPPET_LIMIT_SECONDS) {
        audio.pause();
        setIsPlaying(false);
        // Snap back so subsequent presses also stop at limit
        audio.currentTime = SNIPPET_LIMIT_SECONDS;
        if (!dismissedForRef.current.has(String(ct.id))) {
          setSnippetUnlockTrack(ct);
        }
      }
    };
    // Block user from seeking past the snippet cap
    const handleSeeking = () => {
      const ct = currentTrackRef.current;
      if (ct && ct.preview_mode === 'snippet' && !isUnlocked(ct.id) && audio.currentTime > SNIPPET_LIMIT_SECONDS) {
        audio.currentTime = SNIPPET_LIMIT_SECONDS;
      }
    };
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => playNextRandom();
    const handleError = (e) => {
      console.warn('Audio error:', e.target?.error?.message || 'unknown');
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('seeking', handleSeeking);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('seeking', handleSeeking);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [playNextRandom]);

  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  // Fetch tracks and set a random one as current (but DON'T autoplay — mobile blocks it)
  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await axios.get(`${API}/tracks`);
        setTracks(data);
        tracksRef.current = data;

        const playable = data.filter(t =>
          t.preview_url &&
          (t.preview_url.endsWith('.mp3') || t.preview_url.endsWith('.wav') ||
           t.preview_url.endsWith('.ogg') || t.preview_url.endsWith('.m4a') ||
           t.preview_url.includes('/api/files/'))
        );
        if (playable.length === 0) return;

        const track = playable[Math.floor(Math.random() * playable.length)];
        setCurrentTrack(track);
        // Pre-load the audio source (but don't play)
        audioRef.current.src = normalizeAudioUrl(track.preview_url);
        audioRef.current.preload = 'metadata';

        // Try autoplay (works on desktop, silently fails on mobile — that's fine)
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          ensureAudioContext();
          axios.post(`${API}/tracks/${track.id}/play`).catch(() => {});
        } catch {
          // Autoplay blocked on mobile — user will tap play manually
        }
      } catch {
        // Tracks fetch failed
      }
    };
    init();
  }, [ensureAudioContext]);

  // Helper: post a "play" hit to the correct endpoint based on track type.
  const recordPlay = (track) => {
    if (!track?.id) return;
    const endpoint = track.unlock_item_type === 'featured_track'
      ? `${API}/featured-artists/tracks/${track.id}/play`
      : `${API}/tracks/${track.id}/play`;
    axios.post(endpoint).catch(() => {});
  };

  const playTrack = async (track) => {
    const audio = audioRef.current;

    // Initialize AudioContext on first user-triggered play (required for iOS/Android)
    ensureAudioContext();

    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (err) {
          console.warn('Play failed:', err.message);
          setIsPlaying(false);
        }
      }
      return;
    }

    setCurrentTrack(track);
    // Fresh play session — allow the unlock modal to show again for this track
    if (track?.id) dismissedForRef.current.delete(String(track.id));

    if (track.preview_url) {
      const url = normalizeAudioUrl(track.preview_url);
      audio.src = url;
      audio.preload = 'auto';

      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.warn('Play failed:', err.message);
        setIsPlaying(false);
      }

      // Record play
      recordPlay(track);
    }
  };

  const getPlayableTracks = useCallback(() => {
    return tracksRef.current.filter(t =>
      t.preview_url &&
      (t.preview_url.endsWith('.mp3') || t.preview_url.endsWith('.wav') ||
       t.preview_url.endsWith('.ogg') || t.preview_url.endsWith('.m4a') ||
       t.preview_url.includes('/api/files/'))
    );
  }, []);

  const skipNext = useCallback(async () => {
    ensureAudioContext();
    // Prefer user queue first
    if (queueRef.current.length > 0) {
      const next = queueRef.current[0];
      const remaining = queueRef.current.slice(1);
      queueRef.current = remaining;
      setQueue(remaining);
      setCurrentTrack(next);
      audioRef.current.src = normalizeAudioUrl(next.preview_url);
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        if (next.id) recordPlay(next);
      } catch { setIsPlaying(false); }
      return;
    }
    const playable = getPlayableTracks();
    if (playable.length === 0) return;
    const currentIdx = playable.findIndex(t => t.id === currentTrack?.id);
    const next = playable[(currentIdx + 1) % playable.length];
    setCurrentTrack(next);
    audioRef.current.src = normalizeAudioUrl(next.preview_url);
    try {
      await audioRef.current.play();
      setIsPlaying(true);
      axios.post(`${API}/tracks/${next.id}/play`).catch(() => {});
    } catch { setIsPlaying(false); }
  }, [currentTrack, getPlayableTracks, ensureAudioContext]);

  const skipPrev = useCallback(async () => {
    ensureAudioContext();
    // If more than 3 seconds in, restart current track
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    const playable = getPlayableTracks();
    if (playable.length === 0) return;
    const currentIdx = playable.findIndex(t => t.id === currentTrack?.id);
    const prev = playable[(currentIdx - 1 + playable.length) % playable.length];
    setCurrentTrack(prev);
    audioRef.current.src = normalizeAudioUrl(prev.preview_url);
    try {
      await audioRef.current.play();
      setIsPlaying(true);
      axios.post(`${API}/tracks/${prev.id}/play`).catch(() => {});
    } catch { setIsPlaying(false); }
  }, [currentTrack, getPlayableTracks, ensureAudioContext]);

  const pause = () => {
    audioRef.current.pause();
    setIsPlaying(false);
  };

  const play = async () => {
    if (currentTrack?.preview_url) {
      ensureAudioContext();
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    }
  };

  const seek = (time) => {
    const ct = currentTrackRef.current;
    let target = time;
    if (ct && ct.preview_mode === 'snippet' && !isUnlocked(ct.id)) {
      if (time > SNIPPET_LIMIT_SECONDS) {
        // User tried to scrub past the snippet cap → surface the upsell.
        setSnippetUnlockTrack(ct);
        target = SNIPPET_LIMIT_SECONDS;
      }
    }
    audioRef.current.currentTime = target;
    setCurrentTime(target);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // MediaSession API — enables lock screen / notification controls on mobile
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    if (currentTrack) {
      const artworkUrl = currentTrack.cover_image
        ? (currentTrack.cover_image.startsWith('/api/')
          ? `${window.location.origin}${currentTrack.cover_image}`
          : currentTrack.cover_image)
        : '';

      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.title || 'Unknown Track',
        artist: currentTrack.artist || 'Unknown Artist',
        album: currentTrack.album || '',
        ...(artworkUrl ? { artwork: [
          { src: artworkUrl, sizes: '512x512', type: 'image/png' },
        ] } : {}),
      });
    }

    navigator.mediaSession.setActionHandler('play', () => play());
    navigator.mediaSession.setActionHandler('pause', () => pause());
    navigator.mediaSession.setActionHandler('previoustrack', () => skipPrev());
    navigator.mediaSession.setActionHandler('nexttrack', () => skipNext());
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime != null) seek(details.seekTime);
    });

    return () => {
      ['play', 'pause', 'previoustrack', 'nexttrack', 'seekto'].forEach(action => {
        try { navigator.mediaSession.setActionHandler(action, null); } catch {}
      });
    };
  }, [currentTrack, play, pause, skipNext, skipPrev, seek]);

  return (
    <PlayerContext.Provider value={{
      tracks,
      currentTrack,
      isPlaying,
      currentTime,
      duration,
      volume,
      setVolume,
      playTrack,
      pause,
      play,
      seek,
      skipNext,
      skipPrev,
      formatTime,
      analyserRef,
      // Queue API
      queue,
      addToQueue,
      removeFromQueue,
      clearQueue,
      moveInQueue,
      // Snippet / unlock API
      snippetUnlockTrack,
      dismissUnlock: () => {
        const t = snippetUnlockTrack;
        if (t?.id) dismissedForRef.current.add(String(t.id));
        setSnippetUnlockTrack(null);
      },
      markUnlocked,
      isUnlocked,
      SNIPPET_LIMIT_SECONDS,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
}
